// The applet runtime, all of it: the store (the applets installed in this browser, IndexedDB), the loader (their
// modules from memory), their lifecycle (install, uninstall, enable, disable, activate, deactivate, restart, reload,
// update), the context an applet is activated with, and the host behind @webtop/api (client/sdk/api.js) and
// @webtop/components. An applet runs in the page itself: its modules are loaded from memory through blob: URLs (the
// loader, below), its @webtop/api and
// @webtop/components imports go to modules made here for it alone (so the API knows which applet calls it), and
// everything it registers (views, widgets, launchers, services, listeners, timers, dialogs) is a disposable the
// runtime keeps and drops when it is deactivated, so a restart leaves nothing behind. Permissions (its manifest's)
// gate the API's namespaces; they do not fence the page off from an applet's code, which runs with the page's own
// rights: install applets you trust (docs/applets.md). The Applets overlay, Load Applet, publishing and the Settings
// card are the Applets applet's (applets/applets), which manages them through @webtop/api's `applets` namespace.
//
// Activation: `onStartup` applets at page start (start()); one with widgets when a tile of its shows (the widget's
// placeholder asks through setAppletHost); `onLaunch` ones when launched (the launcher, the overlay, another applet).
// An applet exports activate(context) (optional), deactivate() (optional) and launch(context) (optional: what
// Launch does once it is active; without it, Launch activates it and opens the views it contributes).
//
// Developer Mode (Settings > Applets, this browser's localStorage `webtop.developer`): off, an applet whose manifest
// says `environment: 'dev'` (the demos, a developer's tools) is neither listed (the Applets overlay, the launcher, the
// Add section, the Opens field's suggestions) nor activated (activate() refuses it, so no path loads one), and the
// overlay offers no Load Applet, Reload or Publish. An installed copy from before its manifest said so is judged by
// what the server says of the same id (devApplet).
//
// Required applets (manifest `required`, built into the server: the launcher, Settings, audio, storage, the assistant)
// are part of webtop itself: the page shell names them (`<meta name="webtop-required">`), and they are installed (or
// brought to the server's version), enabled and started in every browser, Developer
// Mode or not; they cannot be uninstalled or disabled. Where this browser cannot keep them (no IndexedDB), their files stay in memory for the visit. The page
// reaches one through the service it registers (appletService()).
//
// Third-party applets (APPLET_THIRD_PARTY in the server's .env, the shell's `<meta name="webtop-third-party">`): off,
// only what comes with webtop runs here, the required applets and the general ones (`<meta name="webtop-general">`).
// A published or loaded copy this browser already has stays in its database, unread and never activated (mayRun), and
// there is no Developer Mode, and so no developer applet and nothing to load. The server enforces its part (what it
// lists and serves); the rest cannot bind someone at their own browser's console, only keep anyone from being talked into it.
//
// Commands: what a `command` tile runs (`@<applet>/<command>` in the shortcut dialog's Opens field, model.js). The
// manifest declares them (contributes.commands), the code connects each with commands.register; running one
// activates the applet first, so a tile works whether or not it is active. Only declared or registered commands run,
// never an arbitrary function of the module.
import { __connect, __token, scoped } from '@webtop/api';
import {
  appletSummary, checkManifest, fileType, ID_RE, isPackagePath, isScript, MANIFEST_FILE, NAME_RE, newerVersion, PACKAGE_MAX,
} from '/applet-model.js';
import { lineIcon, SCALE } from '../widgets/kit.js';
import { appletWidgets, refreshWidget, registerAppletWidget, setAppletHost, widgetInstances } from '../widgets/applet.js';
import * as db from './db.js';
import { goTo } from './nav.js';
import { render } from './render.js';
import { API, bus, setWebtop, slug, webtop } from './state.js';
import { forgetWebtop, isHomePinned, launchTile, moveWebtopEntries, readRecent, writeHome, writeRecent } from './notch.js';
import { addTiles } from './shortcuts.js';
import { runPageAction, ACTIONS as PAGE_ACTIONS } from './actions.js';
import { channelId, onChannel } from './channel.js';
import { screenH, screenW, sizeKey, spotOf } from './geometry.js';
import { addTab, mic, openTab, registerPart, shownTab, speaker } from './notch-panel.js';
import { openView } from './overlay.js';
import { showMenu } from './menu.js';
import { forgetDroppedIcons, forgetTileIcons } from './tiles.js';
import { renderLocked } from './lock.js';
import { api, confirmDialog, modal, toast } from './ui.js';
import { getTargets, listViews, mountView, registerTarget } from './views.js';
import { readZip, writeZip } from './zip.js';

// ---- the store: the applets installed in this browser (IndexedDB store `applets`, client/lib/db.js) and what they keep
// (`applet-data`). An installed applet is one version of it: its manifest, where it came from and its files, every
// byte, so it runs again without the server. The record is kept apart from the files, so listing the applets reads no
// file.
//   meta   {id, version, manifest, source: 'server' | 'zip' | 'folder', enabled, installedAt, size, files (count),
//           folder? (a FileSystemDirectoryHandle, kept for Reload where the browser allows it), hash? (a required
//           applet's: of the files the server had)}
//   files  {path: Uint8Array}, under `files:<id>`
// Its data: `storage` of @webtop/api, one value per key under `<id>:<key>` in `applet-data`.
const FILES = (id) => `files:${id}`;
const isMeta = (key) => !key.startsWith('files:');

/** Every installed applet's record, by id. Never rejects. */
async function listInstalled() {
  const out = [];
  for (const key of await db.keys(db.APPLETS)) {
    if (!isMeta(key)) continue;
    const meta = await db.get(db.APPLETS, key);
    if (meta?.manifest) out.push(meta);
  }
  return out.sort((a, b) => a.manifest.name.localeCompare(b.manifest.name));
}
const getInstalled = (id) => db.get(db.APPLETS, id);
/** The files of an installed applet, {path: Uint8Array}, or null. */
const getFiles = async (id) => (await db.get(db.APPLETS, FILES(id))) ?? null;

/**
 * Installs (or replaces) an applet: `manifest` as checkManifest answered, `files` a Map or object of path -> Uint8Array.
 * A version already installed is replaced; `enabled` is kept from the old record. Answers the record.
 */
async function storeInstall({ manifest, files, source, folder = null, hash = null }) {
  const old = await getInstalled(manifest.id);
  const map = files instanceof Map ? Object.fromEntries(files) : files;
  let size = 0;
  for (const bytes of Object.values(map)) size += bytes.byteLength;
  const meta = {
    id: manifest.id, version: manifest.version, manifest, source, enabled: old?.enabled ?? true,
    installedAt: new Date().toISOString(), size, files: Object.keys(map).length, folder, ...(hash && { hash }),
  };
  if (!(await db.put(db.APPLETS, FILES(manifest.id), map))) throw new Error('This browser could not keep the applet (no room, or storage is off)');
  if (!(await db.put(db.APPLETS, manifest.id, meta))) throw new Error('This browser could not keep the applet (no room, or storage is off)');
  return meta;
}
async function storeSetEnabled(id, enabled) {
  const meta = await getInstalled(id);
  if (!meta) return null;
  meta.enabled = !!enabled;
  await db.put(db.APPLETS, id, meta);
  return meta;
}
/** Removes an applet, its files and its data. */
async function storeUninstall(id) {
  await db.remove(db.APPLETS, [id, FILES(id)]);
  await dataClear(id);
}

// ---- an applet's own data (storage.get / set / remove / keys / clear of @webtop/api)
const dataKey = (id, key) => `${id}:${key}`;
const dataGet = (id, key) => db.get(db.APPLET_DATA, dataKey(id, key));
async function dataSet(id, key, value) {
  if (!(await db.put(db.APPLET_DATA, dataKey(id, key), value))) throw new Error('This browser could not keep the value (no room, or storage is off)');
}
const dataRemove = (id, key) => db.remove(db.APPLET_DATA, [dataKey(id, key)]);
async function dataKeys(id) {
  const prefix = `${id}:`;
  return (await db.keys(db.APPLET_DATA)).filter((k) => k.startsWith(prefix)).map((k) => k.slice(prefix.length));
}
async function dataClear(id) {
  const keys = await dataKeys(id);
  if (keys.length) await db.remove(db.APPLET_DATA, keys.map((k) => dataKey(id, k)));
}
const store = {
  listInstalled, getInstalled, getFiles, install: storeInstall, setEnabled: storeSetEnabled, uninstall: storeUninstall,
  dataGet, dataSet, dataRemove, dataKeys, dataClear,
};

// ---- the loader: an applet's modules, loaded from memory. The files of an installed applet are plain ES modules that
// import each other by relative path and the shared modules by bare name (`react`, `@webtop/api`), so each one becomes
// a blob: URL with every import rewritten to the blob: URL (or bare name's address) it means, and the entry is
// imported from there. The browser's own module loader runs them: no bundling, no service worker, and it works on the
// plain-HTTP LAN address, where a service worker could not. The cost: an import's specifier must be a string literal (a
// computed `import(name)` cannot be rewritten) and a module's own address is a blob: URL, so a file next to it is
// reached through context.asset(path) rather than `new URL('x.png', import.meta.url)`. Modules are rewritten on
// demand, dependencies first; a circular import between two modules cannot be given a blob: URL before it exists and
// is refused with a readable message.
const SPECIFIERS = [
  // import x from '...', import {a} from '...', import * as x from '...', import '...' (with attributes after)
  /(\bimport\s*(?:(?:[\w$]+\s*,?\s*)?(?:\{[^}]*\}|\*\s*as\s+[\w$]+)?\s*from\s*)?)(['"])([^'"\n]+)\2/g,
  // export {a} from '...', export * from '...', export * as x from '...'
  /(\bexport\s+(?:\*(?:\s*as\s+[\w$]+)?|\{[^}]*\})\s*from\s*)(['"])([^'"\n]+)\2/g,
  // import('...')
  /(\bimport\s*\(\s*)(['"])([^'"\n]+)\2/g,
];
const decoder = new TextDecoder();

/** The package path `spec` means from the module at `from`, or null for a bare name or an absolute address. */
function resolvePath(spec, from) {
  if (/^[a-z][a-z0-9+.-]*:/i.test(spec)) return null; // http:, blob:, data:
  if (!spec.startsWith('./') && !spec.startsWith('../') && !spec.startsWith('/')) return null;
  const base = spec.startsWith('/') ? [] : from.split('/').slice(0, -1);
  for (const seg of spec.replace(/^\//, '').split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') { if (!base.length) return null; base.pop(); } else base.push(seg);
  }
  const path = base.join('/');
  return isPackagePath(path) ? path : null;
}

/**
 * loadApplet({id, files, bare}) -> {urlOf(path), moduleUrl(path), revoke()}: `files` is {path: Uint8Array},
 * `bare` maps a bare name (`@webtop/api`, `react`) to the address it loads from. moduleUrl(path) builds the blob:
 * module for a script and everything it imports; urlOf(path) is a blob: URL for any file (an image); textOf(path) is
 * a file's text (a stylesheet). revoke() frees them all (when the applet is deactivated).
 */
function loadApplet({ id, files, bare = {} }) {
  const urls = new Map(); // path -> blob: URL
  const building = new Set();
  const blobUrl = (path, body, type) => {
    const url = URL.createObjectURL(new Blob([body], { type }));
    urls.set(path, url);
    return url;
  };
  function urlOf(path) {
    if (urls.has(path)) return urls.get(path);
    if (!(path in files)) throw new Error(`${id}: no file ${path} in the applet`);
    return isScript(path) ? moduleUrl(path) : blobUrl(path, files[path], fileType(path));
  }
  function moduleUrl(path) {
    if (urls.has(path)) return urls.get(path);
    if (!(path in files)) throw new Error(`${id}: no module ${path} in the applet`);
    if (building.has(path)) throw new Error(`${id}: ${path} is in a circular import, which an applet cannot have`);
    building.add(path);
    try {
      let src = decoder.decode(files[path]);
      for (const re of SPECIFIERS) {
        src = src.replace(re, (whole, head, quote, spec) => {
          const target = resolvePath(spec, path);
          if (target) return `${head}${quote}${urlOf(target)}${quote}`;
          if (Object.hasOwn(bare, spec)) return `${head}${quote}${bare[spec]}${quote}`;
          return whole; // an absolute address, or a bare name the import map may know
        });
      }
      src += `\n//# sourceURL=webtop-applet://${id}/${path}`;
      return blobUrl(path, src, 'text/javascript');
    } finally { building.delete(path); }
  }
  return {
    urlOf,
    moduleUrl,
    textOf(path) {
      if (!(path in files)) throw new Error(`${id}: no file ${path} in the applet`);
      return decoder.decode(files[path]);
    },
    revoke() {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
    },
  };
}

/** The shared modules' addresses as the page shell's import map gives them (server/lib/assets.js): bare name -> address. */
function importMap() {
  try { return JSON.parse(document.querySelector('script[type="importmap"]')?.textContent ?? '{}').imports ?? {}; } catch { return {}; }
}

// ---- the runtime
const installed = new Map(); // id -> meta (the store, above)
const active = new Map(); // id -> instance {id, meta, module, loader, blobs, subscriptions, styles, timers, context, status, error, pending}
const launchers = new Map(); // `${applet}/${id}` -> {applet, id, label, icon, action}
const commands = new Map(); // `${applet}/${name}` -> fn (commands.register)
const services = new Map(); // `${applet}/${name}` -> impl
const events = new EventTarget(); // what applets listen to (events.on): webtop, render, applet, visibility, applet:<id>:<name>
const SERVER_TTL = 60_000;
let available = null; // {at, list}: the server's applets (GET /api/applets), kept a minute
let availableRequest = null;
const DEV_KEY = 'webtop.developer';
/** Whether Developer Mode is on in this browser. */
export function developerMode() {
  if (!thirdPartyEnabled()) return false;
  try { return JSON.parse(localStorage.getItem(DEV_KEY))?.on === true; } catch { return false; }
}
// A developer applet ('dev'), by its installed manifest or, for a copy from before the field, the server's entry.
const devApplet = (id, manifest = installed.get(id)?.manifest) => manifest?.environment === 'dev'
  || available?.list.find((a) => a.id === id)?.environment === 'dev';
/** Whether the applet `id` may be listed and loaded here: any, in Developer Mode; else all but developer applets. */
export const appletAllowed = (id, manifest) => developerMode() || !devApplet(id, manifest);
/**
 * Turns Developer Mode on or off: the developer applets this webtop uses (or that start with the page) are activated,
 * or every active one is deactivated; whatever lists applets is drawn again.
 */
export async function setDeveloperMode(on) {
  if (on && !thirdPartyEnabled()) throw new Error(THIRD_PARTY_OFF);
  try { localStorage.setItem(DEV_KEY, JSON.stringify({ on: !!on })); } catch {}
  for (const meta of installed.values()) {
    if (!devApplet(meta.id)) continue;
    if (!on) await deactivate(meta.id);
    else if (meta.enabled && (meta.manifest.activationEvents.includes('onStartup') || usedOnWebtop(meta.id))) await activate(meta.id).catch(toast);
  }
  emitChange();
  render();
}
/** Whether this server takes third-party applets (APPLET_THIRD_PARTY in its .env; the page shell's meta tag, server/routes/pages.js). */
export const thirdPartyEnabled = () => document.querySelector('meta[name="webtop-third-party"]')?.content === 'on';
const THIRD_PARTY_OFF = 'This server only runs the applets that come with webtop';
/** Whether `id` is a built-in applet that may run with third-party applets off (the page shell's meta tag). */
const isGeneral = (id) => (document.querySelector('meta[name="webtop-general"]')?.content ?? '').split(',').includes(id);
/** The required applets the server names (the page shell's meta tag): [{id, version, hash}], hash of its files. */
export function requiredApplets() {
  const raw = document.querySelector('meta[name="webtop-required"]')?.content ?? '';
  return raw.split(',').map((s) => s.trim()).filter(Boolean).map((s) => {
    const [id, version, hash = ''] = s.split('@');
    return { id, version, hash };
  }).filter((r) => ID_RE.test(r.id) && r.version);
}
export const isRequired = (id) => requiredApplets().some((r) => r.id === id);
// Whether an installed copy may run here. By where it came from too: a ZIP that took a built-in's id is not the built-in.
const mayRun = (meta) => thirdPartyEnabled() || isRequired(meta.id) || (meta.source === 'server' && isGeneral(meta.id));
let required = null; // resolves once every required applet is installed at the server's version (ensureRequired)
// Resolves once init() has run: modules that init() before this one (their Settings cards) wait for it.
let initialized;
const initDone = new Promise((resolve) => { initialized = resolve; });
const memoryFiles = new Map(); // id -> files: a required applet this browser could not keep
const filesOf = async (id) => memoryFiles.get(id) ?? store.getFiles(id);
let loaded = null; // resolves once the installed applets are read from the browser's database (init)
let installedRead = false; // and this says it has
// Applets being installed or removed: a tile redrawn meanwhile (deactivating redraws) must not activate them again.
const busy = new Set();
const KINDS = { success: 1, info: 1, error: 1, busy: 1 };

export const isActive = (id) => active.get(id)?.status === 'active';
export const instanceOf = (id) => active.get(id) ?? null;
export const installedApplets = () => [...installed.values()];
export const installedApplet = (id) => installed.get(id) ?? null;
const emit = (name, detail) => events.dispatchEvent(new CustomEvent(name, { detail }));
const emitChange = () => emit('applets', {});
/** Runs `fn` on every change to the installed or active applets (the overlay redraws). */
export const onAppletsChange = (fn) => { events.addEventListener('applets', fn); return () => events.removeEventListener('applets', fn); };

// ---- the server's applets: built-in ones and published ones, as GET /api/applets lists them
export async function availableApplets({ fresh = false } = {}) {
  if (!fresh && available && Date.now() - available.at < SERVER_TTL) return available.list;
  availableRequest ??= api('GET', '/api/applets').then((list) => { available = { at: Date.now(), list }; return list; })
    .finally(() => { availableRequest = null; });
  return availableRequest;
}
export const availableApplet = async (id) => (await availableApplets()).find((a) => a.id === id) ?? null;
/** The newer version the server has for an installed applet, or null. */
export function updateFor(meta) {
  const entry = available?.list.find((a) => a.id === meta.id);
  return entry && newerVersion(entry.version, meta.version) ? entry : null;
}

// ---- lifecycle
export async function activate(id) {
  const known = active.get(id);
  if (known) { await known.pending; return known; }
  const meta = installed.get(id) ?? await store.getInstalled(id);
  if (!meta) throw new Error(`The applet "${id}" is not installed`);
  if (!mayRun(meta)) throw new Error(THIRD_PARTY_OFF);
  if (!meta.enabled && !isRequired(id)) throw new Error(`The applet “${meta.manifest.name}” is disabled`);
  if (!isRequired(id) && !appletAllowed(id, meta.manifest)) throw new Error(`${meta.manifest.name} is a developer applet: turn on Developer Mode in Settings › Applets`);
  const inst = {
    id, meta, module: null, loader: null, blobs: [], subscriptions: [], styles: [], timers: new Set(), context: null,
    status: 'loading', error: null, since: Date.now(), pending: null,
  };
  active.set(id, inst);
  emitChange();
  inst.pending = (async () => {
    const files = await filesOf(id);
    if (!files) throw new Error('its files are missing: install it again');
    // The shared modules' addresses, absolute: a blob: module (the loader, above) cannot resolve a relative one.
    const map = Object.fromEntries(Object.entries(importMap()).map(([name, url]) => [name, new URL(url, location.href).href]));
    const bare = Object.fromEntries(Object.entries(map).filter(([name]) => !name.startsWith('/')));
    // The applet's own @webtop/api and @webtop/components: modules that take the shared ones scoped to it. The
    // address imported is the versioned one the import map holds, so the page and the applet share one instance.
    bare['@webtop/api'] = ownModule(inst, `import { scoped } from '${map['@webtop/api'] ?? new URL('/sdk/api.js', location.href).href}';\n`
      + `const api = scoped('${__token(id)}');\nexport default api;\n`
      + 'export const { storage, notifications, views, windows, widgets, launchers, commands, events, webtop, services, channel, page, backend, applets, notch, disposables } = api;\n');
    bare['@webtop/components'] = ownModule(inst, `import { componentsFor } from '${map['@webtop/components'] ?? new URL('/sdk/components.js', location.href).href}';\n`
      + `export const { View, Widget, Modal, Toast, Launcher, useWidget, run, React } = componentsFor('${__token(id)}');\n`);
    inst.loader = loadApplet({ id, files, bare });
    for (const path of meta.manifest.styles) addStyle(inst, path);
    inst.context = makeContext(inst);
    if (meta.manifest.main) {
      inst.module = await import(inst.loader.moduleUrl(meta.manifest.main));
      if (typeof inst.module.activate === 'function') await inst.module.activate(inst.context);
    }
    inst.status = 'active';
    emit('applet', { id, state: 'active' });
    emitChange();
  })().catch((e) => {
    console.error(`applet ${id}:`, e);
    disposeInstance(inst);
    active.delete(id);
    inst.status = 'failed';
    inst.error = e.message;
    emitChange();
    throw new Error(`${meta.manifest.name}: ${e.message}`);
  });
  await inst.pending;
  return inst;
}
export async function deactivate(id) {
  const inst = active.get(id);
  if (!inst) return;
  try { await inst.pending; } catch { return; }
  try { await inst.module?.deactivate?.(); } catch (e) { console.error(`applet ${id}: deactivate:`, e); }
  disposeInstance(inst);
  active.delete(id);
  emit('applet', { id, state: 'inactive' });
  emitChange();
  render(); // its widgets' tiles show the placeholder
}
export async function restart(id) {
  await deactivate(id);
  return activate(id);
}
/** Launch: activate, then the module's launch(context), else open the views it contributes (one window each). */
export async function launch(id) {
  const inst = await activate(id);
  if (typeof inst.module?.launch === 'function') return inst.module.launch(inst.context);
  if (!listViews(id).length) {
    for (const v of inst.meta.manifest.contributes.views) {
      if (!listViews(id).some((h) => h.title === v.name)) mountView({ applet: id, id: v.id, title: v.name, target: v.defaultTarget }, `${inst.meta.manifest.name} has nothing to show here yet.`);
    }
  }
  return undefined;
}
function disposeInstance(inst) {
  for (const d of inst.subscriptions.splice(0).reverse()) dispose(d);
  for (const h of listViews(inst.id)) h.dispose();
  for (const key of [...launchers.keys()]) if (launchers.get(key).applet === inst.id) launchers.delete(key);
  for (const key of [...services.keys()]) if (key.startsWith(`${inst.id}/`)) services.delete(key);
  for (const key of [...commands.keys()]) if (key.startsWith(`${inst.id}/`)) commands.delete(key);
  for (const t of inst.timers) { clearTimeout(t); clearInterval(t); }
  inst.timers.clear();
  for (const style of inst.styles.splice(0)) style.remove();
  inst.loader?.revoke();
  for (const url of inst.blobs.splice(0)) URL.revokeObjectURL(url);
}
const dispose = (d) => { try { if (typeof d === 'function') d(); else d?.dispose?.(); } catch (e) { console.error(e); } };
function ownModule(inst, src) {
  const url = URL.createObjectURL(new Blob([src], { type: 'text/javascript' }));
  inst.blobs.push(url);
  return url;
}
// A <style>, not a <link> to a blob: URL: Safari resolves a url() in a custom property against the sheet using it, so
// var(--icon-search) in a blob: sheet asks for an address that cannot exist, and shows nothing.
function addStyle(inst, path) {
  const style = document.createElement('style');
  style.textContent = inst.loader.textOf(path);
  style.dataset.applet = inst.id;
  document.head.append(style);
  inst.styles.push(style);
  return () => { style.remove(); inst.styles = inst.styles.filter((s) => s !== style); };
}
// What activate(context) gets (docs/applets.md).
function makeContext(inst) {
  const { id, meta } = inst;
  const apiOf = scoped(__token(id));
  const timer = (set, clear) => (fn, ms, ...args) => {
    const t = set(() => { if (set === setTimeout) inst.timers.delete(t); fn(...args); }, ms);
    inst.timers.add(t);
    return t;
  };
  return Object.freeze({
    id, version: meta.version, manifest: meta.manifest, permissions: Object.freeze([...meta.manifest.permissions]), api: apiOf,
    subscriptions: Object.freeze({ add: (d) => { inst.subscriptions.push(d); return d; }, dispose: () => { for (const d of inst.subscriptions.splice(0).reverse()) dispose(d); } }),
    mount: (what, opts) => apiOf.views.mount(what, opts),
    asset: (path) => inst.loader.urlOf(String(path)),
    style: (path) => addStyle(inst, String(path)),
    setTimeout: timer(setTimeout), setInterval: timer(setInterval),
    clearTimeout: (t) => { clearTimeout(t); inst.timers.delete(t); }, clearInterval: (t) => { clearInterval(t); inst.timers.delete(t); },
    log: (...args) => console.log(`[${id}]`, ...args),
    backend: apiOf.backend,
  });
}

// ---- mounting (host.views.mount): what an applet hands over is an Element, a function (el, handle) => cleanup, or
// {component, props} for a React component drawn with @webtop/components
function mount(id, what, opts = {}) {
  const { target = 'window', title = '', ...rest } = opts ?? {};
  const handle = mountView({ applet: id, title, target, ...rest }, what instanceof Node ? what : null);
  if (typeof what === 'function') {
    const cleanup = what(handle.el, handle);
    if (typeof cleanup === 'function') handle.onDispose(cleanup);
  } else if (what && typeof what === 'object' && !(what instanceof Node) && what.component) {
    import('@webtop/components').then(({ renderReact }) => {
      if (handle.disposed) return;
      const r = renderReact(handle.el, what.component, what.props ?? {});
      handle.setProps = (props) => r.update(props);
      handle.onDispose(() => r.unmount());
    }).catch((e) => { handle.el.textContent = `Could not load React: ${e.message}`; });
  }
  track(id, handle);
  return handle;
}
function track(id, d) {
  const inst = active.get(id);
  if (inst) inst.subscriptions.push(d);
  else dispose(d); // registered after deactivation: nothing keeps it
  return d;
}

// A dialog of the page's look with an applet's content (notifications.dialog): {title, content: Element | string,
// buttons: [{label, primary?, danger?, action?}], backdrop?, onClose} -> {el, close()}. `backdrop: false` keeps it open on a
// click outside it.
function dialog(id, { title = '', content = null, buttons = [{ label: 'Close' }], backdrop = true, onClose } = {}) {
  const dlg = document.createElement('dialog');
  dlg.className = 'applet-dialog';
  dlg.dataset.applet = id;
  const form = document.createElement('form');
  form.method = 'dialog';
  const h = document.createElement('h2');
  h.textContent = String(title);
  const body = document.createElement('div');
  body.className = 'applet-dialog-body';
  if (content instanceof Node) body.append(content); else if (content != null) body.textContent = String(content);
  const menu = document.createElement('menu');
  for (const b of Array.isArray(buttons) ? buttons : []) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = String(b?.label ?? 'OK');
    if (b?.primary) btn.classList.add('primary');
    if (b?.danger) btn.classList.add('danger');
    btn.addEventListener('click', async () => { try { if (await b?.action?.() !== false) dlg.close(); } catch (e) { toast(e); } });
    menu.append(btn);
  }
  form.append(h, body, menu);
  dlg.append(form);
  document.body.append(dlg);
  modal(dlg, { backdrop });
  const handle = { el: body, close: () => dlg.close(), dispose: () => dlg.close() };
  dlg.addEventListener('close', () => { dlg.remove(); try { onClose?.(); } catch (e) { console.error(e); } }, { once: true });
  dlg.showModal();
  track(id, handle);
  return handle;
}

// ---- the host @webtop/api talks to
const host = {
  permissions: (id) => new Set(installed.get(id)?.manifest.permissions ?? active.get(id)?.meta.manifest.permissions ?? []),
  track,
  notch: {
    mic,
    speaker,
    addTab,
    openTab,
    // Every applet's sound goes through these, so only one that is part of webtop may provide them.
    register(id, name, impl) {
      if (!isRequired(id)) throw new Error('Only an applet that is part of webtop can register with the notch.');
      return registerPart(name, impl);
    },
  },
  storage: {
    get: (id, key, o) => (o?.scope === 'webtop' ? Promise.resolve(webtop?.applets?.[id]?.[key]) : store.dataGet(id, key)),
    set: (id, key, value, o) => (o?.scope === 'webtop' ? patchWebtopData(id, { [key]: value }) : store.dataSet(id, key, value)),
    remove: (id, key, o) => (o?.scope === 'webtop' ? patchWebtopData(id, { [key]: null }) : store.dataRemove(id, key).then(() => {})),
    keys: (id, o) => (o?.scope === 'webtop' ? Promise.resolve(Object.keys(webtop?.applets?.[id] ?? {})) : store.dataKeys(id)),
    clear: (id, o) => (o?.scope === 'webtop' ? patchWebtopData(id, null) : store.dataClear(id)),
  },
  ui: {
    toast: (id, text, o) => toast(String(text).slice(0, 300), { kind: KINDS[o?.kind] ? o.kind : undefined }),
    confirm: (id, o) => confirmDialog({ title: String(o?.title ?? 'Are You Sure?'), text: String(o?.text ?? ''), button: String(o?.button ?? 'OK'), danger: !!o?.danger, alt: o?.alt ? String(o.alt) : null }),
    dialog,
  },
  views: {
    targets: getTargets,
    mount,
    registerTarget: (id, def) => registerTarget({ ...def, capabilities: def?.capabilities ?? ['dock'] }, id),
    list: (id) => listViews(id),
  },
  widgets: {
    register: (id, def) => registerAppletWidget(id, def),
    list: (id) => appletWidgets().filter((w) => w.applet === id),
    instances: widgetInstances,
    refresh: refreshWidget,
  },
  launchers: {
    register(id, def) {
      if (!def?.id || typeof def.action !== 'function') throw new Error('A launcher needs an id and an action');
      const key = `${id}/${def.id}`;
      launchers.set(key, { applet: id, id: String(def.id), label: String(def.label ?? def.id), icon: def.icon ?? null, action: def.action });
      return () => { launchers.delete(key); };
    },
  },
  commands: {
    register(id, name, fn) {
      if (typeof fn !== 'function') throw new Error('A command needs a function');
      const key = `${id}/${name}`;
      commands.set(key, fn);
      return () => { if (commands.get(key) === fn) commands.delete(key); };
    },
    list: (id) => commandsOf(id).map((k) => k.id),
  },
  events: {
    on(id, name, fn) {
      const handler = (e) => { try { fn(e.detail); } catch (err) { console.error(`applet ${id}:`, err); } };
      events.addEventListener(name, handler);
      return () => events.removeEventListener(name, handler);
    },
    emit: (id, name, detail) => emit(`applet:${id}:${name}`, detail),
  },
  webtop: {
    current: () => (webtop ? { slug: webtop.slug, title: webtop.title, loaded: true, canEdit: !!webtop.canEdit, unlocked: !!webtop.unlocked, locked: !!webtop.locked, owned: !!webtop.owned, storage: webtop.storage ?? 'remote' } : { slug, title: null, loaded: false, canEdit: false, unlocked: false, locked: true, owned: false, storage: 'remote' }),
    tiles: () => (webtop?.shortcuts ?? []).map((s) => ({ id: s.id, kind: s.kind, title: s.title, ...spotOf(s), size: sizeKey(s), target: ['url', 'webtop', 'command'].includes(s.kind) ? s.target : undefined })),
    goTo: (href, o) => goTo(href, o),
    recent: () => readRecent().map((r) => ({ slug: r.slug, title: r.title })),
    clearRecent: () => { writeRecent([]); },
    moveEntries: (from, to) => moveWebtopEntries(from, to),
    humanProved,
    // A newer record of the open webtop (one the server answered with after changing it): shown in place of the
    // page's copy. A record of another webtop is ignored (setWebtop); answers whether it was shown.
    replace(d) {
      if (!d || typeof d !== 'object' || d.slug !== slug || !Array.isArray(d.shortcuts)) return false;
      forgetDroppedIcons(webtop?.shortcuts, d.shortcuts); // tiles it no longer has, or shows with another icon
      setWebtop(d);
      render();
      return true;
    },
    // The open webtop's record as this page holds it (the server's pub(): no password data), or null while none is in.
    record: () => (webtop ? structuredClone(webtop) : null),
    // A webtop's tile as the page draws it everywhere (the launcher's recents, the Webtops section): its marks and its
    // menu, going there when clicked. `entry` is a list entry ({slug, title, locked, owned, mine, unlocked, color, ...}).
    tile: (entry) => launchTile(entry),
    // The page lets go of a webtop that no longer exists: its tiles' icons (when it is the open one), its recents and
    // favorite entries, and its Home pin.
    async forget(s) {
      if (webtop?.slug === s) await forgetTileIcons(webtop.shortcuts);
      forgetWebtop(s);
      if (isHomePinned(s)) writeHome(null);
    },
    addTiles: (tiles, o) => addTiles(tiles, o),
    // The open webtop fetched again (who may edit it changed: a sign-in, a sign-out); a locked one shows the lock screen.
    async reload() {
      if (!slug) return; // no webtop at this address
      try { setWebtop(await api('GET', API)); render(); } catch (e) { if (e.locked) renderLocked(); else if (!e.stale) throw e; }
    },
  },
  // The live channel: what the server sends this applet's backend's way (`applet:<id>:<event>`, server/lib/
  // backend-server.js), and the channel's own connect / disconnect.
  channel: {
    id: () => channelId(),
    on: (id, event, fn) => onChannel(event === 'connect' || event === 'disconnect' ? event : `applet:${id}:${event}`, (detail) => {
      try { fn(detail); } catch (e) { console.error(`applet ${id}:`, e); }
    }),
  },
  // The page's own actions (client/lib/actions.js) and what it shows.
  page: {
    run: (id, name, args) => runPageAction(name, args),
    actions: () => Object.keys(PAGE_ACTIONS),
    info: () => ({ view: openView() ?? shownTab(), space: { width: Math.round(screenW()), height: Math.round(screenH()) }, scale: SCALE }),
    menu: (x, y, items) => showMenu(Number(x) || 0, Number(y) || 0, items),
  },
  services: {
    register(id, name, impl) {
      const key = `${id}/${name}`;
      if (services.has(key)) throw new Error(`The service "${key}" is already registered`);
      services.set(key, impl);
      return () => { if (services.get(key) === impl) services.delete(key); };
    },
    get: (full) => services.get(full) ?? null,
    list: () => [...services.keys()],
  },
  backend: {
    fetch: (id, path, init) => fetch(`/api/applets/${encodeURIComponent(id)}/backend/${path.replace(/^\/+/, '')}`, init),
  },
  applets: {
    self: (id) => structuredClone(installed.get(id)?.manifest ?? active.get(id)?.meta.manifest ?? null),
    list: () => [...installed.values()].map((m) => ({ ...appletSummary(m.manifest), enabled: m.enabled, active: isActive(m.id), source: m.source })),
    isActive,
    launch: (id) => launch(id),
    // Managing them (the `applets` permission: the Applets applet, applets/applets).
    thirdParty: thirdPartyEnabled,
    installed: () => [...installed.values()].map((m) => ({
      id: m.id, version: m.version, source: m.source, enabled: m.enabled, files: m.files, size: m.size, folder: !!m.folder,
      manifest: structuredClone(m.manifest),
    })),
    offered: async (o) => structuredClone(await availableApplets({ fresh: o?.fresh === true })),
    isRequired,
    allowed: (id) => appletAllowed(id),
    launchable: (id) => { const m = installed.get(id); return !!m && !!LAUNCHABLE(m); },
    updateFor: (id) => { const m = installed.get(id); const u = m && updateFor(m); return u ? structuredClone(u) : null; },
    icon: (id) => { const m = installed.get(id); return m ? iconFor(m) ?? Promise.resolve(null) : Promise.resolve(null); },
    install: (id, version) => installFromServer(id, version ?? null),
    installZip: (bytes) => installFromZip(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes), 'zip'),
    installFolder: (from) => installFromFolder(from),
    uninstall, setEnabled, restart, reload, update,
    package: (id) => appletPackage(id),
    launchers: () => [...launchers.values()].map((l) => ({ ...l })),
    developerMode, setDeveloperMode,
    onChange: (fn) => onAppletsChange(() => { try { fn(); } catch (e) { console.error(e); } }),
  },
};
// The webtop-scoped values of an applet (storage with {scope: 'webtop'}): a PATCH of the open webtop's `applets`
// field, for its editors; `patch` null drops the applet's values.
async function patchWebtopData(id, patch) {
  if (!webtop) throw new Error('No webtop is open');
  if (!webtop.canEdit) throw new Error('This webtop cannot be edited here');
  setWebtop(await api('PATCH', API, { applets: { [id]: patch } }));
}

// ---- installing
/**
 * Installs an applet from the files of a package (`files`: Map or {path: Uint8Array}, manifest.json at the root or
 * inside one top folder), from `source` ('server' | 'zip' | 'folder'); `folder` keeps a directory handle for Reload.
 * A version already installed is replaced (deactivated first); the applet is activated again when it was active, has
 * `onStartup`, or the open webtop shows one of its widgets.
 */
export async function installFiles(files, source, { folder = null, quiet = false } = {}) {
  if (source !== 'server' && !thirdPartyEnabled()) throw new Error(THIRD_PARTY_OFF);
  const { manifest, flat } = unpack(files);
  const wasActive = active.has(manifest.id);
  busy.add(manifest.id);
  let meta;
  try {
    if (wasActive) await deactivate(manifest.id);
    meta = await store.install({ manifest, files: flat, source, folder });
    installed.set(meta.id, meta);
  } finally { busy.delete(manifest.id); }
  emitChange();
  if (!quiet) toast(`${meta.manifest.name} ${meta.version} installed`);
  if (meta.enabled && appletAllowed(meta.id) && (wasActive || meta.manifest.activationEvents.includes('onStartup') || usedOnWebtop(meta.id))) {
    await activate(meta.id).catch(toast);
  }
  render();
  return meta;
}
export async function installFromZip(bytes, source = 'zip', extra = {}) {
  return installFiles(await readZip(bytes), source, extra);
}
export async function installFromServer(id, version = null) {
  const entry = await availableApplet(id);
  if (!entry) throw new Error(`The server has no applet "${id}"`);
  const v = version ?? entry.version;
  toast(`Installing ${entry.name}…`);
  const res = await fetch(`/api/applets/${encodeURIComponent(id)}/${encodeURIComponent(v)}/package`);
  if (!res.ok) throw new Error(`Could not download ${entry.name} ${v} (${res.status})`);
  return installFromZip(new Uint8Array(await res.arrayBuffer()), 'server');
}
/** Installs from a FileList of a folder (an <input webkitdirectory>) or a FileSystemDirectoryHandle (showDirectoryPicker). */
export async function installFromFolder(from) {
  const files = new Map();
  let folder = null;
  if (from && typeof from.values === 'function' && !(from instanceof FileList)) {
    folder = from;
    if ((await folder.queryPermission?.({ mode: 'read' })) === 'prompt') await folder.requestPermission?.({ mode: 'read' });
    await walk(folder, '', files);
  } else {
    for (const f of from) {
      const path = (f.webkitRelativePath || f.name).split('/').slice(1).join('/'); // the picked folder's own name goes
      if (path && !/(^|\/)(node_modules|\.git)\//.test(path)) files.set(path, new Uint8Array(await f.arrayBuffer()));
    }
  }
  return installFiles(files, 'folder', { folder });
}
async function walk(dir, prefix, files) {
  for await (const [name, handle] of dir.entries()) {
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'directory') { if (name !== 'node_modules' && name !== '.git') await walk(handle, path, files); } else files.set(path, new Uint8Array(await (await handle.getFile()).arrayBuffer()));
    if (files.size > 2000) throw new Error('The folder has too many files');
  }
}
// The manifest and the files below its folder, checked (applet-model.js).
function unpack(files) {
  const entries = [...(files instanceof Map ? files : Object.entries(files))];
  let prefix = '';
  if (!entries.some(([p]) => p === MANIFEST_FILE)) {
    const tops = new Set(entries.map(([p]) => p.split('/')[0]));
    const top = tops.size === 1 ? [...tops][0] : null;
    if (!top || !entries.some(([p]) => p === `${top}/${MANIFEST_FILE}`)) throw new Error(`No ${MANIFEST_FILE} in the package`);
    prefix = `${top}/`;
  }
  const flat = {};
  let size = 0;
  for (const [p, bytes] of entries) {
    if (!p.startsWith(prefix)) continue;
    const data = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    size += data.byteLength;
    flat[p.slice(prefix.length)] = data;
  }
  if (size > PACKAGE_MAX) throw new Error(`The package is too big (${Math.round(PACKAGE_MAX / 1048576)} MB at most)`);
  let raw;
  try { raw = JSON.parse(new TextDecoder().decode(flat[MANIFEST_FILE])); } catch (e) { throw new Error(`${MANIFEST_FILE} is not valid JSON: ${e.message}`); }
  const manifest = checkManifest(raw);
  for (const [what, path] of [['main', manifest.main], ['icon', manifest.icon], ...manifest.styles.map((s) => ['styles', s])]) {
    if (path && !(path in flat)) throw new Error(`manifest: "${what}" names ${path}, which is not in the package`);
  }
  return { manifest, flat };
}
const usedOnWebtop = (id) => (webtop?.shortcuts ?? []).some((s) => s.kind === 'applet' && s.data?.applet === id);

const refuseRequired = (id) => {
  if (isRequired(id)) throw new Error(`${installed.get(id)?.manifest.name ?? id} is part of webtop: it cannot be uninstalled or disabled`);
};
export async function uninstall(id) {
  refuseRequired(id);
  busy.add(id);
  try {
    await deactivate(id);
    await store.uninstall(id);
    installed.delete(id);
  } finally { busy.delete(id); }
  emitChange();
  render();
}
export async function setEnabled(id, on) {
  if (!on) refuseRequired(id);
  const meta = await store.setEnabled(id, on);
  if (!meta) return;
  installed.set(id, meta);
  emitChange();
  if (!on) await deactivate(id);
  else if (appletAllowed(id) && (meta.manifest.activationEvents.includes('onStartup') || usedOnWebtop(id))) await activate(id).catch(toast);
  render();
}
/** Reload: a folder applet is read again from its folder (where the browser kept the handle), then restarted. */
export async function reload(id) {
  const meta = installed.get(id);
  if (!meta) throw new Error('Not installed');
  if (meta.source === 'folder' && meta.folder) {
    await installFromFolder(meta.folder);
    return;
  }
  await restart(id);
  toast(`${meta.manifest.name} restarted`);
}
export async function update(id) {
  const meta = installed.get(id);
  const entry = meta && updateFor(meta);
  if (!entry) throw new Error('There is no newer version on the server');
  return installFromServer(id, entry.version);
}
/** An installed applet's files as they are here, as a ZIP (what the Applets applet publishes and exports). */
export async function appletPackage(id) {
  if (!installed.has(id)) throw new Error('Not installed');
  const files = await filesOf(id);
  if (!files) throw new Error('Its files are missing: install it again');
  return writeZip(files);
}

// ---- commands: what `command` tiles run
// An applet's commands: the ones its manifest declares, then any its code registered beyond those (while active).
function commandsOf(id) {
  const declared = installed.get(id)?.manifest.contributes.commands ?? [];
  const extra = [...commands.keys()].filter((k) => k.startsWith(`${id}/`)).map((k) => k.slice(id.length + 1))
    .filter((name) => !declared.some((k) => k.id === name)).map((name) => ({ id: name, name, about: '', params: [] }));
  return [...declared, ...extra];
}
// The short name an applet goes by in an Opens field: the last part of its id (dev.webtop.hello -> hello) when no other
// installed applet's id ends the same, else the whole id.
function shortName(id) {
  const last = id.split('.').pop();
  return [...installed.keys()].some((other) => other !== id && other.split('.').pop() === last) ? id : last;
}
// A command target's parts: `dev.webtop.hello/toast?text=Hi` -> {id, name: 'toast', query: 'text=Hi'}. The query is
// split off first, as its values may hold anything once decoded.
function targetParts(target) {
  const s = String(target);
  const q = s.indexOf('?');
  const [id, name = ''] = (q < 0 ? s : s.slice(0, q)).split('/');
  return { id, name, query: q < 0 ? '' : s.slice(q + 1) };
}
/** What a `command` tile's Opens field shows: `@hello/toast?text=Hi` for `dev.webtop.hello/toast?text=Hi`. */
export function commandText(target) {
  const { id, name, query } = targetParts(target);
  return `@${shortName(id)}${name ? `/${name}` : ''}${query ? `?${query}` : ''}`;
}
/**
 * The target `@<applet>[/<command>[?args]]` names, or an Error message: the applet by its id, the last part of its id
 * or its name, among those installed, then those the server offers; an id that matches none is kept as typed (it may
 * be installed later, or in another browser). Arguments are kept URL-encoded (`?text=Hi there` -> `?text=Hi+there`).
 */
export function commandTarget(text) {
  const raw = String(text).trim().replace(/^@/, '');
  const q = raw.indexOf('?');
  const [who, name, more] = (q < 0 ? raw : raw.slice(0, q)).split('/');
  const key = (who ?? '').toLowerCase();
  if (!key || more !== undefined) return { error: 'An applet: @hello, or one of its commands: @hello/toast' };
  if (name !== undefined && !NAME_RE.test(name)) return { error: 'A command: lower-case letters, digits and dashes' };
  const query = q < 0 ? '' : new URLSearchParams(raw.slice(q + 1)).toString();
  if (q >= 0 && name === undefined) return { error: 'Arguments go with a command: @hello/toast?text=Hi' };
  if (query.length > 500) return { error: 'The arguments are too long (500 characters at most, encoded)' };
  const slugOf = (n) => String(n ?? '').toLowerCase().trim().replace(/\s+/g, '-');
  const known = [...installed.values()].map((m) => ({ id: m.id, name: m.manifest.name }))
    .concat((available?.list ?? []).map((a) => ({ id: a.id, name: a.name })));
  const found = known.find((a) => a.id === key) ?? known.find((a) => a.id.split('.').pop() === key) ?? known.find((a) => slugOf(a.name) === key);
  const id = found?.id ?? key;
  if (!ID_RE.test(id)) return { error: `There is no applet “${who}”` };
  return { target: name ? `${id}/${name}${query ? `?${query}` : ''}` : id };
}
/** Whether a `command` tile can run here: true until the installed applets are read, so no tile flashes a warning. */
export function commandReady(target) {
  const { id } = targetParts(target);
  return !installedRead || (!!installed.get(id)?.enabled && appletAllowed(id));
}
/**
 * Runs a `command` tile's target: launches the applet (the id alone) or runs one of its commands, activating it first,
 * with its arguments as an object (`?text=Hi` -> {text: 'Hi'}). An applet this browser does not have is offered for
 * installing from the server, a disabled one for enabling, and one whose installed version lacks the command for
 * updating, when the server's newer version has it.
 */
export async function runCommand(target) {
  await loaded;
  const { id, name, query } = targetParts(target);
  let meta = installed.get(id);
  if (!meta) {
    const entry = await availableApplet(id).catch(() => null);
    if (!entry) return toast(`This shortcut needs the applet “${id}”, which this browser does not have and the server does not offer`, { kind: 'info' });
    if (!appletAllowed(id)) return toast(`${entry.name} is a developer applet: turn on Developer Mode in Settings › Applets`, { kind: 'info' });
    if (!await confirmDialog({ title: `Install ${entry.name}?`, text: `This shortcut runs the applet ${entry.name}, which is not installed in this browser.`, button: 'Install' })) return undefined;
    meta = await installFromServer(id);
  }
  if (!meta.enabled) {
    if (!await confirmDialog({ title: `Enable ${meta.manifest.name}?`, text: `This shortcut runs ${meta.manifest.name}, which is disabled in this browser.`, button: 'Enable' })) return undefined;
    await setEnabled(id, true);
  }
  if (!appletAllowed(id)) return toast(`${meta.manifest.name} is a developer applet: turn on Developer Mode in Settings › Applets`, { kind: 'info' });
  if (!name) return launch(id);
  await activate(id);
  let fn = commands.get(`${id}/${name}`);
  if (!fn) {
    await availableApplets().catch(() => null);
    const newer = updateFor(meta);
    if (!newer?.commands?.some((k) => k.id === name)) throw new Error(`${meta.manifest.name} has no command “${name}”`);
    if (!await confirmDialog({ title: `Update ${meta.manifest.name}?`, text: `This shortcut runs “${name}”, which ${meta.manifest.name} ${meta.version} does not have; version ${newer.version} on the server does.`, button: 'Update' })) return undefined;
    await update(id);
    await activate(id);
    fn = commands.get(`${id}/${name}`);
    if (!fn) throw new Error(`${meta.manifest.name} has no command “${name}”`);
  }
  return fn(Object.fromEntries(new URLSearchParams(query)));
}
/** A promise of the address of an applet's icon for a tile: the installed one's file, else the server's copy, else null. */
export async function appletIcon(id) {
  await loaded;
  const meta = installed.get(id);
  if (meta) return iconFor(meta);
  const entry = await availableApplet(id).catch(() => null);
  return entry?.icon ? `/applets/${encodeURIComponent(id)}/${encodeURIComponent(entry.version)}/${entry.icon}` : null;
}
/**
 * The Opens field's suggestions for applets, for what is typed in it: each enabled installed applet that launches or
 * has commands, and, once the text names one (`@hello`, `@hello/...`), its commands, a command that takes arguments
 * again with them left to fill in (`@hello/toast?text=`); `title` is what the shortcut is named when one is picked.
 */
export function commandSuggestions(typed = '') {
  const text = String(typed).trim().toLowerCase();
  const out = [];
  for (const m of installed.values()) {
    if (!m.enabled || !m.manifest.main || !appletAllowed(m.id)) continue;
    const self = commandText(m.id);
    const cmds = commandsOf(m.id);
    if (LAUNCHABLE(m)) out.push({ value: self, label: `Open ${m.manifest.name}`, title: m.manifest.name });
    else if (cmds.length) out.push({ value: self, label: `${m.manifest.name}: its commands`, title: m.manifest.name });
    if (text !== self && !text.startsWith(`${self}/`)) continue;
    for (const k of cmds) {
      const value = commandText(`${m.id}/${k.id}`);
      out.push({ value, label: `${m.manifest.name}: ${k.name}`, title: k.name });
      const params = k.params ?? [];
      if (params.length) out.push({ value: `${value}?${params.map((p) => `${p.id}=`).join('&')}`, label: `${k.name}: ${params.map((p) => p.about || p.id).join(', ')}`, title: k.name });
    }
  }
  return out;
}

// ---- what the launcher and the Add section list
export const LAUNCHABLE = (m) => m.enabled && m.manifest.main && (m.manifest.type === 'application' || m.manifest.activationEvents.includes('onLaunch') || m.manifest.contributes.views.length);
/**
 * The Add section's entries: the widgets every enabled installed applet declares in its manifest (so one not
 * active yet is offered; its tile activates it), and any an active applet registered beyond those.
 */
export function appletWidgetEntries() {
  const seen = new Set();
  const out = [];
  const add = (applet, w, icon) => {
    const key = `${applet}/${w.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      label: w.name, about: w.about || `A widget of ${installed.get(applet)?.manifest.name ?? applet}`, icon: icon ?? lineIcon('applets'),
      match: `${w.id}\n${applet}\napplet`, kind: 'applet', fields: { data: { applet, widget: w.id } }, size: w.sizes[0],
    });
  };
  for (const w of appletWidgets()) add(w.applet, w, w.icon);
  for (const m of installed.values()) if (m.enabled && appletAllowed(m.id)) for (const w of m.manifest.contributes.widgets) add(m.id, w, null);
  return out;
}
// An installed applet's icon as something an <img> shows (a blob: URL of its icon file), or null; kept per version.
const icons = new Map();
export function iconFor(meta) {
  if (!meta.manifest.icon) return null;
  const key = `${meta.id}@${meta.version}`;
  if (!icons.has(key)) {
    icons.set(key, filesOf(meta.id).then((files) => {
      const bytes = files?.[meta.manifest.icon];
      return bytes ? URL.createObjectURL(new Blob([bytes], { type: fileType(meta.manifest.icon) })) : null;
    }).catch(() => null));
  }
  return icons.get(key);
}

// Event wiring and first drawing, run once by client.js in the order the parts of the page depend on.
export function init() {
  __connect(host); // the module applets get too (the import map names one address for it)
  // With third-party applets off only what comes with webtop is read and run, so nothing else is listed; a tile of
  // anything else says why it is empty.
  loaded = store.listInstalled().then((list) => {
    for (const meta of list) if (mayRun(meta)) installed.set(meta.id, meta);
    installedRead = true;
    emitChange();
    if (webtop?.shortcuts?.some((s) => s.kind === 'command')) render(); // their tiles' marks (commandReady)
  });
  required = ensureRequired();
  initialized();
  // With third-party applets off, a widget tile works only for what comes with webtop: the rest say so.
  setAppletHost({
    nameOf: (id) => installed.get(id)?.manifest.name ?? available?.list.find((a) => a.id === id)?.name ?? null,
    async need(id) {
      if (!thirdPartyEnabled() && !isRequired(id) && !isGeneral(id)) return 'off';
      await (isRequired(id) ? required : loaded); // a tile drawn before the installed list is read waits for it
      if (busy.has(id)) return 'installing'; // the redraw after this settles it
      if (isActive(id)) return 'active';
      const meta = installed.get(id);
      if (meta) {
        if (!meta.enabled) return 'disabled';
        if (!appletAllowed(id)) return 'dev';
        try { await activate(id); return 'active'; } catch (e) { toast(e); return 'unknown'; }
      }
      try {
        const entry = await availableApplet(id);
        return !entry ? 'unknown' : appletAllowed(id) ? 'available' : 'dev';
      } catch { return 'unknown'; }
    },
    install: (id) => installFromServer(id),
    enable: (id) => setEnabled(id, true),
  });
  // what applets may listen to (events.on): the page's own happenings
  bus.addEventListener('webtop', (e) => emit('webtop', host.webtop.current()));
  bus.addEventListener('render', () => emit('render', { slug }));
  document.addEventListener('visibilitychange', () => emit('visibility', { hidden: document.hidden }));
}
/** After the first render: the installed applets are read and the onStartup ones activated (the Applets applet then applies required updates). */
export async function start() {
  await required;
  // the server's list first, when anything starts with the page: it says which older copies are developer applets
  if ([...installed.values()].some((m) => m.enabled && m.manifest.activationEvents.includes('onStartup'))) await availableApplets().catch(() => null);
  await Promise.all([...installed.values()]
    .filter((m) => !isRequired(m.id) && m.enabled && appletAllowed(m.id) && m.manifest.activationEvents.includes('onStartup'))
    .map((m) => activate(m.id).catch(toast)));
}

// The required applets, each brought to the version the server names (downloaded only when this browser has another
// version, other files (`hash`) or none), enabled, and activated when it starts with the page. A failure is shown, and that applet left out.
async function ensureRequired() {
  await loaded;
  await Promise.all(requiredApplets().map(async ({ id, version, hash }) => {
    try {
      let meta = installed.get(id);
      if (!meta || meta.version !== version || meta.hash !== hash || !(await filesOf(id))) meta = await installRequired(id, version, hash);
      if (!meta.enabled) {
        meta = (await store.setEnabled(id, true)) ?? { ...meta, enabled: true };
        installed.set(id, meta);
      }
      if (meta.manifest.activationEvents.includes('onStartup')) await activate(id);
    } catch (e) {
      console.error(`applet ${id}:`, e);
      toast(new Error(`${installed.get(id)?.manifest.name ?? id} (part of webtop) could not start: ${e.message}`));
    }
  }));
  emitChange();
}
async function installRequired(id, version, hash) {
  const res = await fetch(`/api/applets/${encodeURIComponent(id)}/${encodeURIComponent(version)}/package`);
  if (!res.ok) throw new Error(`could not download it (${res.status})`);
  const { manifest, flat } = unpack(await readZip(new Uint8Array(await res.arrayBuffer())));
  if (manifest.id !== id) throw new Error(`the server's package is ${manifest.id}`);
  await deactivate(id);
  let meta;
  try {
    meta = await store.install({ manifest, files: flat, source: 'server', hash });
    memoryFiles.delete(id);
  } catch {
    memoryFiles.set(id, flat); // this browser keeps nothing: the files stay for this visit
    meta = { id, version: manifest.version, hash, manifest, source: 'server', enabled: true, installedAt: new Date().toISOString(), size: 0, files: Object.keys(flat).length, folder: null };
  }
  installed.set(id, meta);
  return meta;
}
// The services of the required applets the page itself uses (applets/settings, applets/launcher, applets/storage,
// applets/assistant, applets/audio).
export const SETTINGS = 'dev.webtop.settings/settings';
export const LAUNCHER = 'dev.webtop.launcher/launcher';
export const STORAGE = 'dev.webtop.storage/storage'; // applets/storage: `_` webtops, assets, More › Storage
export const ASSISTANT = 'dev.webtop.assistant/assistant'; // applets/assistant: push to talk (the notch's button)
export const AUDIO = 'dev.webtop.audio/audio'; // applets/audio: all the sound, and the notch's parts for it
export const USER = 'dev.webtop.user/user'; // applets/user: signing in
export const APPLETS = 'dev.webtop.applets/applets'; // applets/applets: the Applets overlay
export const WEBTOPS = 'dev.webtop.webtops/webtops'; // applets/webtops: the Webtops section, New / Rename / Delete / Export / Import
/** The Webtops applet's service, `fn` run with it (a failure is a toast). */
export const webtops = (fn) => appletService(WEBTOPS).then(fn).catch(toast);
// Who is signed in, for the page's menus and dialogs (the user applet's; not signed in until it is up), and its
// sign-in, which asks and answers as its service does (true once signed in).
export const signedIn = () => !!serviceNow(USER)?.signedIn();
export const loginAvailable = () => !!serviceNow(USER)?.available();
/**
 * Whether an answer asked for the human check (a 403 {human}: a new webtop's first write, sending a sign-in code) and this
 * browser has now passed it, so the request is worth sending again. The check is the user applet's dialog.
 */
export async function humanProved(res) {
  if (res.status !== 403 || !res.clone) return false; // a `_` webtop's answer is the storage applet's, and never asks
  const what = (await res.clone().json().catch(() => null))?.human;
  if (typeof what !== 'string') return false;
  return appletService(USER).then((u) => u.prove(what)).catch(() => false);
}
export const showLogin = () => appletService(USER).then((u) => u.showLogin()).catch((e) => { toast(e); return false; });
/** The storage applet's service (once it is up), for what the page asks of it. */
export const storage = () => appletService(STORAGE);
// A webtop's assets, the storage applet's one way in and one way out (applets/storage/src/assets.js): putAsset(file or
// address, slug?) -> {url} to write into the record, assetSrc(ref) -> what an <img> or url() shows (never rejects).
export const putAsset = (...args) => storage().then((s) => s.putAsset(...args));
export const assetSrc = (ref) => storage().then((s) => s.assetSrc(ref)).catch(() => ref);
/** A service as it is now: its applet's, when that is active, else null (for a read that cannot wait). */
export const serviceNow = (full) => (isActive(String(full).split('/')[0]) ? services.get(full) ?? null : null);
/**
 * Runs fn(service) once the service is up, and again each time its applet is activated anew (a restart forgets what
 * was registered with it): how the page puts its own things into a required applet (its Settings cards).
 */
export function withService(full, fn) {
  const id = String(full).split('/')[0];
  let last = null; // the instance fn last ran for
  const run = () => appletService(full).then((svc) => {
    const inst = active.get(id);
    if (!inst || inst === last) return;
    last = inst;
    fn(svc);
  }).catch((e) => console.error(`${full}:`, e));
  events.addEventListener('applet', (e) => { if (e.detail?.id === id && e.detail.state === 'active') run(); });
  run();
}
/**
 * A service an applet registered (services.register), `<applet id>/<name>`: its applet is activated first (a
 * required one once it is installed), so a caller (the page's launcher hooks) gets it whenever the applet can run.
 */
export async function appletService(full) {
  const id = String(full).split('/')[0];
  await initDone;
  await (isRequired(id) ? required : loaded);
  await activate(id);
  const svc = services.get(full);
  if (!svc) throw new Error(`${installed.get(id)?.manifest.name ?? id} offers no service “${String(full).slice(id.length + 1)}”`);
  return svc;
}
